import React from "react";
import "./App.css";
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from "react-router-dom";
import FawnNav from "./components/Nav";
import LandingPage from "./pages/LandingPage";
import AgentPage from "./pages/AgentPage";
import ClientPage from "./pages/ClientPage";
import AdminPage from "./pages/AdminPage";
import EnrollPage from "./pages/EnrollPage";
import LoginPage from "./pages/LoginPage";
import Modal from "react-modal";
import QuotePage from "./pages/QuotePage";
import ClaimPage from "./pages/ClaimPage";
import ErrorBoundary from "./components/ErrorBoundary";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import RequireAuth from "./auth/RequireAuth";
import { ToastProvider } from "./ui";
import AppShell from "./appshell/AppShell";
import PlaceholderPage from "./appshell/pages/PlaceholderPage";
import KitPage from "./appshell/pages/KitPage";
import { STAFF_PAGES, REFERRAL_PAGES, ADMIN_PAGES } from "./appshell/navConfig";
import TodayPage from "./features/today/TodayPage";
import BookPage from "./features/book/BookPage";
import ClientHubPage from "./features/clients/ClientHubPage";
import HomePage from "./features/home/HomePage";
import ReferPage from "./features/refer/ReferPage";
import RequestsPage from "./features/service/RequestsPage";
import ServiceQueuePage from "./features/service/ServiceQueuePage";
import PipelinePage from "./features/pipeline/PipelinePage";
import CommissionsPage from "./features/commissions/CommissionsPage";
import DataFixesPage from "./features/custodian/DataFixesPage";
import { ShellIndexRedirect, StaffOnly, ReferralOnly } from "./appshell/tierRoutes";
import { FEATURES } from "./config/features";

// Placeholder routes: every nav entry still marked with a `phase`, deduped
// by path (Commissions appears in both tier rails).
const PLACEHOLDER_PAGES = [...STAFF_PAGES, ...REFERRAL_PAGES, ...ADMIN_PAGES].filter(
  (page, i, all) => page.phase && all.findIndex((p) => p.path === page.path) === i
);

Modal.setAppElement("#root");

function QuoteLinkLanding() {
  const location = useLocation();
  return <Navigate to={{ pathname: "/", search: location.search }} replace />;
}

/* Routes live in a child of AuthProvider so pages keep receiving the same
   `user` / `agents` props they always did — now sourced from the single
   central auth subscription instead of a per-page (or per-App) listener. */
function AppRoutes() {
  const { user, agents } = useAuth();
  const location = useLocation();

  // The /app shell and the rebuilt login page carry their own chrome; the
  // legacy top nav renders everywhere else until Phase 4 rebuilds it.
  const hideLegacyNav = location.pathname.startsWith("/app") || location.pathname === "/login";

  return (
    <div className="main">
      {hideLegacyNav ? null : <FawnNav />}
      {/* Each route element is wrapped in its own ErrorBoundary so a render crash on
          one page (e.g. a merged record with absent array fields) degrades to a
          fallback panel instead of white-screening the whole app. Per-route (not one
          boundary around <Routes>) so it resets cleanly on navigation. */}
      <Routes>
        <Route
          path="/"
          element={
            <ErrorBoundary label="the home page">
              <LandingPage />
            </ErrorBoundary>
          }
        />

        <Route
          path="/login"
          element={
            <ErrorBoundary label="the login page">
              <LoginPage />
            </ErrorBoundary>
          }
        />

        {/* Public signup was disabled for security; the old signup screen is gone. */}
        <Route path="/signup" element={<Navigate to="/login" replace />} />

        <Route
          path="/claim"
          element={
            <ErrorBoundary label="the claim page">
              <ClaimPage />
            </ErrorBoundary>
          }
        />

        <Route
          path="/enroll"
          element={
            <ErrorBoundary label="the enrollment page">
              <EnrollPage user={user || ""} agents={agents} />
            </ErrorBoundary>
          }
        />

        {/* /quote (no id) is the referral share-link target (R8). The real
            public quote flow is Phase 4; until then land on the marketing
            page, PRESERVING the ?ref=<agent> attribution param so shared
            links never dead-end and the param survives for the lead form. */}
        <Route path="/quote" element={<QuoteLinkLanding />} />

        <Route
          path="/quote/:id"
          element={
            <ErrorBoundary label="this quote">
              <QuotePage />
            </ErrorBoundary>
          }
        />

        <Route
          path="/mindshift"
          element={
            <ErrorBoundary label="the enrollment page">
              <EnrollPage user={user || ""} agents={agents} source="mindshift" />
            </ErrorBoundary>
          }
        />

        {/* Authenticated legacy pages: RequireAuth waits for Firebase to finish
            restoring the session BEFORE these mount, so their componentDidMount
            fetches always carry a token — refresh / deep links no longer bounce
            to /login (the audit's #5 structural bug). */}
        <Route
          path="/agent"
          element={
            <RequireAuth>
              <ErrorBoundary label="the agent dashboard">
                <AgentPage user={user} agents={agents} />
              </ErrorBoundary>
            </RequireAuth>
          }
        />

        <Route
          path="/client"
          element={
            <RequireAuth>
              <ErrorBoundary label="your dashboard">
                <ClientPage user={user} />
              </ErrorBoundary>
            </RequireAuth>
          }
        />

        <Route
          path="/admin"
          element={
            <RequireAuth>
              <ErrorBoundary label="the admin dashboard">
                <AdminPage user={user} agents={agents} />
              </ErrorBoundary>
            </RequireAuth>
          }
        />

        {/* The redesigned app shell (§2.2): every /app/* view is a real,
            bookmarkable route. Workspace pages render designed placeholders
            until Phases 2–5 deliver them (see navConfig). */}
        <Route
          path="/app"
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route index element={<ShellIndexRedirect />} />
          {/* Phase 2/2.5 workspaces — real pages, tier-gated where a page
              is tier-shaped (UX only; the backend scopes for real).
              Remaining nav entries render placeholders until their phase
              (navConfig marks them). */}
          <Route
            path="today"
            element={
              <StaffOnly>
                <TodayPage />
              </StaffOnly>
            }
          />
          <Route
            path="home"
            element={
              <ReferralOnly>
                <HomePage />
              </ReferralOnly>
            }
          />
          <Route path="book" element={<BookPage />} />
          <Route path="clients/:id" element={<ClientHubPage />} />
          <Route path="pipeline" element={<PipelinePage />} />
          <Route path="refer" element={<ReferPage />} />
          <Route path="requests" element={<RequestsPage />} />
          <Route
            path="service"
            element={
              <StaffOnly>
                <ServiceQueuePage />
              </StaffOnly>
            }
          />
          {/* Commissions (Phase 3, R10): the real page when the flag is on;
              otherwise navConfig keeps its `phase` and PLACEHOLDER_PAGES below
              renders the designed placeholder. Exactly one owns the path. */}
          {FEATURES.commissions ? <Route path="commissions" element={<CommissionsPage />} /> : null}
          {FEATURES.dataFixes ? (
            <Route
              path="datafixes"
              element={
                <StaffOnly>
                  {/* E M-3: a filter chip must never be able to blank the
                      workspace — its own boundary, scoped and reset on nav. */}
                  <ErrorBoundary label="Data fixes">
                    <DataFixesPage />
                  </ErrorBoundary>
                </StaffOnly>
              }
            />
          ) : null}
          {PLACEHOLDER_PAGES.map((page) => (
            <Route
              key={page.path}
              path={page.path.replace("/app/", "")}
              element={<PlaceholderPage page={page} />}
            />
          ))}
          <Route path="kit" element={<KitPage />} />
          <Route path="*" element={<Navigate to="/app/today" replace />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;
